import type { ReactNode } from 'react';
import { CodeBlock, Panel, Tag } from '../../components/ui.tsx';

/**
 * Die Referenz für eigene Alarme. Sie steht bewusst neben den Quellen und
 * nicht in einer separaten Doku: wer gerade einen Token angelegt hat, braucht
 * das Schema im selben Atemzug — und eine Doku, die man woanders pflegt, ist
 * die, die veraltet.
 */

const INGEST_URL = `${window.location.origin}/v1/alerts`;

const CURL = `curl -X POST ${INGEST_URL} \\
  -H "Authorization: Bearer ma_src_DEIN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: 5f2c-1" \\
  -d '{
    "title": "Datenbank antwortet nicht",
    "body": "pg_isready meldet seit 3 Minuten Fehler",
    "severity": "critical",
    "status": "firing",
    "dedup_key": "db-primary-down",
    "labels": { "host": "db-01", "dienst": "postgres" },
    "url": "https://status.example.com/db-01"
  }'`;

const RESOLVE = `curl -X POST ${INGEST_URL} \\
  -H "Authorization: Bearer ma_src_DEIN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{ "title": "Datenbank antwortet nicht", "status": "resolved", "dedup_key": "db-primary-down" }'`;

const RESPONSE = `{
  "accepted": 1,
  "results": [
    { "alert_id": "0f3a…", "deduplicated": false, "deliveries_enqueued": 2 }
  ]
}`;

type FieldDoc = {
  name: string;
  type: string;
  required?: boolean;
  text: string;
};

const FIELDS: FieldDoc[] = [
  { name: 'title', type: 'string, 1–300', required: true, text: 'Die Zeile, die auf dem Sperrbildschirm steht. Was ist kaputt, nicht welcher Check gelaufen ist.' },
  { name: 'body', type: 'string, ≤ 8000', text: 'Details für die Konsole: Messwerte, Logzeile, was hilft.' },
  { name: 'severity', type: 'info | warning | error | critical', text: 'Ohne Angabe gilt der Standard der Quelle. Der Schweregrad setzt die Obergrenze, wie laut zugestellt werden darf.' },
  { name: 'status', type: 'firing | resolved', text: 'Standard firing. resolved schliesst den offenen Alarm mit demselben dedup_key und schickt die Entwarnung.' },
  { name: 'dedup_key', type: 'string, ≤ 200', text: 'Die Identität des Zustands. Gleicher Schlüssel = derselbe Alarm, er zählt hoch statt sich zu vervielfachen. Ohne Angabe wird aus Titel und Labels einer gebildet.' },
  { name: 'labels', type: 'object<string, string>', text: 'Frei wählbare Paare. Weiterleitungen können darauf filtern — hier entsteht, wer nachts geweckt wird.' },
  { name: 'url', type: 'string (URL), ≤ 2000', text: 'Wohin man springt: Dashboard, Runbook, Ticket.' },
  { name: 'occurred_at', type: 'string (ISO 8601)', text: 'Wann es passiert ist, falls nicht jetzt. Standard: Eingangszeitpunkt.' },
];

export function ApiDocs() {
  return (
    <div className="space-y-4">
      <Panel title="Alarm senden">
        <div className="space-y-4">
          <Row label="Endpunkt">
            <code className="font-mono text-[12.5px] text-ink">POST {INGEST_URL}</code>
          </Row>
          <Row label="Authentifizierung">
            <p className="text-[13px] text-ink-2">
              <code className="font-mono text-[12.5px] text-ink">Authorization: Bearer &lt;Token&gt;</code>{' '}
              — der Token einer Quelle. Alternativ{' '}
              <code className="font-mono text-[12.5px] text-ink">X-MeshAlert-Token</code>, wenn das
              sendende System keinen eigenen Authorization-Header setzen kann. Eine Browser-Sitzung
              zählt hier nicht: ein gestohlenes Cookie soll keine Alarme erzeugen können.
            </p>
          </Row>
          <Row label="Antwort">
            <p className="text-[13px] text-ink-2">
              <Tag>202 Accepted</Tag> sobald der Alarm gespeichert ist. Die Zustellung passiert
              danach im Hintergrund — die Antwort sagt also „angenommen", nicht „zugestellt".
            </p>
          </Row>
        </div>
      </Panel>

      <Panel title="Schema" bodyClassName="p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[44rem] border-collapse">
            <thead className="border-b border-line bg-sunken">
              <tr>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.04em] text-ink-3 uppercase">Feld</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.04em] text-ink-3 uppercase">Typ</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.04em] text-ink-3 uppercase">Bedeutung</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-subtle)]">
              {FIELDS.map((field) => (
                <tr key={field.name}>
                  <td className="px-4 py-3 align-top">
                    <code className="font-mono text-[12px] font-medium text-ink">{field.name}</code>
                    {field.required ? (
                      <span className="mt-0.5 block text-[11px] text-critical">Pflicht</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 align-top font-mono text-[11.5px] whitespace-nowrap text-ink-3">
                    {field.type}
                  </td>
                  <td className="px-4 py-3 align-top text-[12.5px] leading-relaxed text-ink-2">
                    {field.text}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Beispiel">
        <CodeBlock copy>{CURL}</CodeBlock>
        <p className="mt-3 mb-1.5 text-[12px] font-medium text-ink-2">Antwort</p>
        <CodeBlock>{RESPONSE}</CodeBlock>
      </Panel>

      <Panel title="Entwarnung">
        <p className="mb-3 text-[13px] text-ink-2">
          Derselbe Endpunkt mit <code className="font-mono text-[12.5px] text-ink">status: "resolved"</code>{' '}
          und demselben <code className="font-mono text-[12.5px] text-ink">dedup_key</code>. Der offene
          Alarm wird geschlossen und die Entwarnung geht über dieselben Wege raus wie der Alarm.
        </p>
        <CodeBlock copy>{RESOLVE}</CodeBlock>
      </Panel>

      <Panel title="Wiederholungen">
        <div className="space-y-3 text-[13px] leading-relaxed text-ink-2">
          <p>
            Schickt ein System denselben <code className="font-mono text-[12.5px] text-ink">dedup_key</code>{' '}
            erneut, entsteht kein zweiter Alarm: der offene zählt hoch und meldet sich erneut. So
            bleibt eine Störung eine Zeile in der Liste, auch wenn der Cronjob sie minütlich meldet.
          </p>
          <p>
            Ein <code className="font-mono text-[12.5px] text-ink">Idempotency-Key</code>-Header geht
            noch einen Schritt weiter: dieselbe Anfrage zweimal gesendet — etwa weil das Netz beim
            ersten Versuch abbrach — liefert dieselbe Antwort und benachrichtigt niemanden doppelt.
            Empfehlenswert bei allem, was automatisch wiederholt.
          </p>
        </div>
      </Panel>

      <Panel title="Fehler">
        <div className="overflow-hidden rounded-md border border-line">
          <table className="w-full border-collapse text-[12.5px]">
            <tbody className="divide-y divide-[var(--border-subtle)]">
              {[
                ['401', 'Token fehlt oder ist ungültig'],
                ['403', 'Die Quelle ist deaktiviert'],
                ['422', 'Der Payload passt nicht zum Schema — details nennt Feld und Grund'],
                ['429', 'Zu viele Anfragen (600 pro Minute je Absender-IP)'],
              ].map(([code, text]) => (
                <tr key={code}>
                  <td className="w-16 px-4 py-2.5 font-mono text-ink">{code}</td>
                  <td className="px-4 py-2.5 text-ink-2">{text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[12.5px] text-ink-3">
          Jede Fehlerantwort hat dieselbe Hülle:{' '}
          <code className="font-mono text-[12px]">{'{ "error": { "code", "message", "details?", "request_id" } }'}</code>
        </p>
      </Panel>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[9rem_1fr] sm:gap-4">
      <span className="text-[12px] font-medium text-ink-3">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
